import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import PatternBox from "../../Patterns/PatternBox";
import Spinner from "../../../components/Spinner/Spinner";
import { Pagination, Grid, Message } from "semantic-ui-react";
import styles from "./MainSection.module.scss";
import { getAllPatterns } from "../../../actions/patternActions";
import { FormattedMessage } from "react-intl";

type Props = {
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  selectedCategories: string[];
  selectedClients: string[];
  selectedFabrics: string[];
  selectedSizes: string[];
  onResultsCountChange?: (count: number) => void;
};

const MainSection: React.FC<Props> = ({
  sortBy,
  sortOrder,
  selectedCategories,
  selectedClients,
  selectedFabrics,
  selectedSizes,
  onResultsCountChange,
}) => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const keyword = searchParams.get('keyword') || '';
  const [currentPage, setCurrentPage] = useState(1);
  const patternsPerPage = 12;

  const allPatterns = useSelector((state: any) => state.allPatterns);
  const { loading, error, patterns, pagination } = allPatterns;

  useEffect(() => {
    // Build filters object
    const filters: any = {};

    if (selectedCategories.length > 0) {
      filters.categories = selectedCategories.join(',');
    }
    if (selectedClients.length > 0) {
      filters.client = selectedClients.join(',');
    }
    if (selectedFabrics.length > 0) {
      filters.fabrics = selectedFabrics.join(',');
    }
    if (selectedSizes.length > 0) {
      filters.sizes = selectedSizes.join(',');
    }

    // URL-driven sort takes priority (from ProductMenu nav links)
    const urlSortBy = searchParams.get('sortBy');
    if (urlSortBy) {
      filters.sortBy = urlSortBy;
      filters.sortOrder = searchParams.get('sortOrder') || 'desc';
    } else if (sortBy) {
      filters.sortBy = sortBy;
      filters.sortOrder = sortOrder;
    }

    // Pass through hasDiscount for "Sale" filter
    const hasDiscount = searchParams.get('hasDiscount');
    if (hasDiscount) {
      filters.hasDiscount = hasDiscount;
    }
    const { categories, ...rest } = filters;
    const newFilters = {
      ...rest,
      ucategory: categories,
    };

    dispatch(getAllPatterns(keyword, currentPage, patternsPerPage, newFilters));
  }, [dispatch, keyword, currentPage, selectedCategories, selectedClients, selectedFabrics, selectedSizes, sortBy, sortOrder, searchParams]);

  const displayPatterns = patterns || [];
  const totalResultsCount = pagination?.totalPatterns ?? displayPatterns.length;

  const handlePageChange = (e: any, { activePage }: any) => {
    setCurrentPage(activePage);
    window.scrollTo(0, 0);
  };

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategories, selectedClients, selectedFabrics, selectedSizes, sortBy, sortOrder, keyword]);

  useEffect(() => {
    if (!onResultsCountChange || loading) {
      return;
    }

    if (error && displayPatterns.length === 0) {
      onResultsCountChange(0);
      return;
    }

    onResultsCountChange(totalResultsCount);
  }, [onResultsCountChange, loading, error, displayPatterns.length, totalResultsCount]);

  return (
    <main className={styles.mainSection}>
      {loading ? (
        <Spinner />
      ) : error && (!patterns || patterns.length === 0) ? (
        <Message negative>
          <Message.Header>
            <FormattedMessage
              id="patterns.error.title"
              defaultMessage="We're sorry we can't display patterns right now"
            />
          </Message.Header>
          <p>{error}</p>
        </Message>
      ) : displayPatterns.length === 0 ? (
        <Message info>
          <Message.Header>
            <FormattedMessage
              id="patterns.noResults.title"
              defaultMessage="No Patterns Found"
            />
          </Message.Header>
          <p>
            <FormattedMessage
              id="patterns.noResults.message"
              defaultMessage="Try adjusting your filters or search terms."
            />
          </p>
        </Message>
      ) : (
        <>
          <div className={styles.resultsRow}>
            <FormattedMessage
              id="patterns.results.short"
              defaultMessage="{count} results"
              values={{ count: totalResultsCount }}
            />
          </div>
          <Grid>
            {displayPatterns.map((pat: any, i: number) => (
              <Grid.Column
                mobile={16}
                tablet={8}
                computer={4}
                key={pat._id || `${pat.title}-${i}`}
              >
                <PatternBox pattern={pat} />
              </Grid.Column>
            ))}
          </Grid>
          {pagination && pagination.totalPages > 1 && (
            <div className={styles.pagination}>
              <Pagination
                activePage={currentPage}
                onPageChange={handlePageChange}
                firstItem={null}
                lastItem={null}
                pointing
                secondary
                totalPages={pagination.totalPages}
              />
            </div>
          )}
        </>
      )}
    </main>
  );
};

export default MainSection;
